import { validateRequiredCsvColumns } from "./RequiredCsvColumns";
import { csvDateFormatIncludesTime, parseCsvDateWithFormats } from "./CsvDateFormats";
import {
  DecisionGraph,
  iterateReleaseDocuments,
  processTemplate,
  Relationship,
  mappingFunctions,
} from "@imminently/decisively-core";
import jsone from "json-e";
import { cloneDeep, filter, groupBy, set } from "lodash";
import Papa from "papaparse";
import { v4 as uuid } from "uuid";
import { JS_BEGINNING_OF_TIME_ISO, JS_END_OF_TIME_ISO } from "./Constants";
import { normalizeMappedEmail } from "./EmailMapping";
import { MappingType, Release, pathWriter, setAtPath } from "./index";

export {
  getAtGenericPath,
  getDerivedTableRecords,
  resolveDeltaSourceRecords,
  runDeltaMapping,
} from "@imminently/decisively-core";

// ============================================================================
// Types
// ============================================================================

export type Mapping = {
  mappedTo?: string;
  workspaces: string[];
  projects: string[];
  releases: string[];
  type?: string;
  disabled?: boolean;
  created: string;
  lastModified: string;
};

export type MappingFields = {
  [field: string]: Mapping;
};

export type ReverseMappingFields = {
  [field: string]: {
    mappedFrom: MappingFields;
    mappedFromAttributes: string[];
    workspaces: string[];
    projects: string[];
    releases: string[];
  };
};

export type MappingsMeta = {
  workspaceInfo: {
    [workspace: string]: {
      name: string;
      projects: string[];
    };
  };
  projectInfo: {
    [project: string]: {
      name: string;
      workspace: string;
      releases: string[];
    };
  };
  releaseInfo: {
    [release: string]: {
      workspace: string;
      project: string;
      number: string;
      env: string;
    };
  };
};

export type Mappings = {
  entity: string;
  fields: MappingFields;
  meta: MappingsMeta;
};

export interface FindMappingFunction {
  (release: any, mappingNameOrId: string, type: string, context?: any): Promise<any> | any;
}

// ============================================================================
// Utility Functions
// ============================================================================

import moment from "moment";

// ============================================================================
// Path Utilities
// ============================================================================

/**
 * Get the parent chain for an entity
 */
export const getParentChain = (entity: string, relationships: Relationship[]): string => {
  const path: string[] = [];
  let currentEntity = entity;

  while (currentEntity !== 'global') {
    const relationship = relationships.find(rel => rel.name === currentEntity);
    if (!relationship || !relationship.name) break;

    path.unshift(relationship.name);
    currentEntity = relationships.find(rel => rel.target === relationship.source)?.name || 'global';
  }

  return path.join('.');
};

/**
 * Get the path from global to the given entity
 */
export const getPathToEntity = (entity: string, relationships: Relationship[]): string[] => {
  const path: string[] = [];
  let currentEntity = entity;

  while (currentEntity) {
    path.unshift(currentEntity);

    const relationship = relationships.find(rel => rel.name === currentEntity);
    if (!relationship || relationship.source === 'global') {
      break;
    }

    const parentRelationship = relationships.find(rel => rel.target === relationship.source);
    if (!parentRelationship) {
      break;
    }

    if (parentRelationship.name) currentEntity = parentRelationship.name;
  }

  return path;
};

// ============================================================================
// Mapping Utilities
// ============================================================================

/**
 * Lists all mappings for a release.
 */
export const listMappings = (release: Release, type?: MappingType): any[] => {
  const documentMappings: any[] = [];

  if (release.documents) {
    iterateReleaseDocuments(release.documents, (document: any, name: string) => {
      if (document.type === "mapping" && document.reference) {
        const documentName = document.name ? document.name.split("/").pop() : name.split("/").pop();
        documentMappings.push({
          name: documentName || "Untitled Mapping",
          path: document.name || name,
          reference: document.reference,
          isDocumentMapping: true,
        });
      }
    });
  }

  const legacyMappings = (release.mapping || []).filter((m: any) => type ? m.type === type : true).map((m: any) => ({
    ...m,
    isDocumentMapping: false,
  }));

  return [...documentMappings, ...legacyMappings];
};

/**
 * Lists all connections for a release.
 */
export const listConnections = (release: Release): any[] => {
  const documentConnections: any[] = [];

  if (release.documents) {
    iterateReleaseDocuments(release.documents, (document: any, name: string) => {
      if (document.type === "connection" && document.reference) {
        const documentName = document.name ? document.name.split("/").pop() : name.split("/").pop();
        documentConnections.push({
          name: documentName || "Untitled Connection",
          path: document.name || name,
          reference: document.reference,
          type: "http",
          isDocumentConnection: true,
        });
      }
    });
  }

  const legacyConnections = (release.connections || []).map((c: any) => ({
    ...c,
    isDocumentConnection: false,
  }));

  return [...documentConnections, ...legacyConnections];
};

export const reverseMappings = (mappings: MappingFields) => {
  const reversed = {} as ReverseMappingFields;
  for (const [attribute, mapping] of Object.entries(mappings)) {
    const prop = mapping.mappedTo ?? attribute;
    if (!reversed[prop]) {
      reversed[prop] = {
        mappedFrom: { [attribute]: mapping },
        mappedFromAttributes: [attribute],
        workspaces: mapping.workspaces,
        projects: mapping.projects,
        releases: mapping.releases,
      };
    } else {
      const existingData = reversed[prop];
      reversed[prop] = {
        mappedFrom: { ...existingData.mappedFrom, [attribute]: mapping },
        mappedFromAttributes: [...new Set([...existingData.mappedFromAttributes, attribute])],
        workspaces: [...new Set([...existingData.workspaces, ...mapping.workspaces])],
        projects: [...new Set([...existingData.projects, ...mapping.projects])],
        releases: [...new Set([...existingData.releases, ...mapping.releases])],
      };
    }
  }
  return reversed;
};

export const getMetaInfoByRelease = (meta: MappingsMeta, release: any) => {
  const releaseInfo = meta.releaseInfo[release];
  const projectInfo = meta.projectInfo[releaseInfo.project];
  const workspaceInfo = meta.workspaceInfo[releaseInfo.workspace];
  return {
    workspace: releaseInfo.workspace,
    workspaceName: workspaceInfo.name,
    project: releaseInfo.project,
    projectName: projectInfo.name,
    release: release,
    releaseEnv: releaseInfo.env,
    releaseNumber: releaseInfo.number,
  };
};

// ============================================================================
// CSV Parsing
// ============================================================================

/**
 * Parse CSV input from base64 string using isomorphic papaparse library
 * Works in both Node.js and browser environments
 * @param input - Base64 encoded CSV string
 */
export const parseCSV = async (input: string): Promise<any[]> => {
  let csvText: string;
  try {
    // Decode base64 to CSV text
    if (typeof (globalThis as any).window === 'undefined' && typeof Buffer !== 'undefined') {
      // Node.js environment
      csvText = Buffer.from(input, 'base64').toString('utf8');
    } else {
      // Browser environment
      csvText = atob(input);
    }
  } catch (error: any) {
    throw new Error(`Failed to decode base64 CSV input: ${error.message || 'Invalid base64 string'}`);
  }

  // Normalize line endings so mixed \r\n / \n does not cause the last row to be dropped
  csvText = csvText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header: string) => header.replace(/^\uFEFF/, "").trim(),
      complete: (results: any) => {
        resolve(results.data);
      },
      error: (error: any) => {
        reject(new Error(`Failed to parse CSV input: ${error.message || 'Unknown error'}`));
      },
    });
  });
};

// ============================================================================
// Mapping Template Functions
// ============================================================================

export const parseMappingTemplate = (mapping: any) => {
  try {
    if (mapping.data !== undefined && mapping.data !== null) {
      return mapping.data;
    }
    if (mapping.cfg !== undefined && mapping.cfg !== null) {
      return JSON.parse(Buffer.from(mapping.cfg, "base64").toString("utf8"));
    }
    throw new Error("Mapping template must have either 'data' or 'cfg' property. Mapping: " + JSON.stringify(mapping));
  } catch (error: any) {
    throw {
      message: `Parsing mapping template failed ${error.message ? ": " + error.message : ""}`,
    };
  }
};

export const applyMapping = (data: any, mapping: any) => {
  let response;
  const c_data = cloneDeep(data);
  const log = false;

  const template = parseMappingTemplate(mapping);

  if (Array.isArray(data)) {
    response = data.map((itemData) => {
      if (log) console.log(JSON.stringify(itemData));
      const result = jsone(template, { ...itemData, ...mappingFunctions });
      return result;
    });
  } else {
    if (log) console.log(JSON.stringify(template));
    response = jsone(template, { ...c_data, ...mappingFunctions });
  }
  if (log) console.log(JSON.stringify(response));
  return response;
};

// ============================================================================
// Main Mapping Functions
// ============================================================================

type EnumMapConfig = {
  exact: Map<string, string>;
  lower: Map<string, string>;
};

const requestEnumMapConfigCache = new WeakMap<object, EnumMapConfig | null>();

const getNonEmptyString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : undefined;
};

const parseQuotedLiteral = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (trimmed.length < 2) return undefined;
  const quote = trimmed[0];
  if ((quote !== '"' && quote !== "'") || trimmed[trimmed.length - 1] !== quote) {
    return undefined;
  }
  const inner = trimmed.slice(1, -1);
  if (quote === '"') {
    return inner.replace(/\\"/g, '"');
  }
  return inner.replace(/\\'/g, "'");
};

const resolveCsvDateBoundaryValue = (source: string, mappingDef: any): string | undefined => {
  const beginningToken = getNonEmptyString(mappingDef?.csvBeginningOfTimeValue);
  if (beginningToken && source === beginningToken) {
    return JS_BEGINNING_OF_TIME_ISO;
  }

  const endToken = getNonEmptyString(mappingDef?.csvEndOfTimeValue);
  if (endToken && source === endToken) {
    return JS_END_OF_TIME_ISO;
  }

  return undefined;
};

const parseEnumMapInput = (input: unknown, columnLabel: string): Record<string, unknown> | undefined => {
  if (input === undefined || input === null) return undefined;
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("enumMap must be a JSON object");
      }
      return parsed as Record<string, unknown>;
    } catch (error: any) {
      throw new Error(`Invalid enum map for column '${columnLabel}': ${error?.message || "must be valid JSON object"}`);
    }
  }
  if (typeof input === "object" && !Array.isArray(input)) {
    return input as Record<string, unknown>;
  }
  throw new Error(`Invalid enum map for column '${columnLabel}': must be object or JSON string`);
};

const getRequestEnumMapConfig = (column: any): EnumMapConfig | undefined => {
  if (!column || typeof column !== "object") return undefined;

  if (requestEnumMapConfigCache.has(column)) {
    return requestEnumMapConfigCache.get(column) || undefined;
  }

  const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
  const enumMapInput = parseEnumMapInput(column?.enumMap, columnLabel);
  if (!enumMapInput) {
    requestEnumMapConfigCache.set(column, null);
    return undefined;
  }

  const exact = new Map<string, string>();
  const lower = new Map<string, string>();
  for (const [rawKey, rawValue] of Object.entries(enumMapInput)) {
    const key = String(rawKey).trim();
    if (!key) continue;
    const mappedValue = rawValue === undefined || rawValue === null ? "" : String(rawValue);
    exact.set(key, mappedValue);
    lower.set(key.toLowerCase(), mappedValue);
  }

  const compiled: EnumMapConfig = { exact, lower };
  requestEnumMapConfigCache.set(column, compiled);
  return compiled;
};

const applyRequestEnumMapping = (value: unknown, column: any): unknown => {
  const columnType = String(column?.type || column?.columnType || "")
    .trim()
    .toLowerCase();
  const enumEnabled = columnType === "enum" || column?.enumMap !== undefined;
  if (!enumEnabled) return value;

  const enumMap = getRequestEnumMapConfig(column);
  const enumDefault = getNonEmptyString(column?.enumDefault);
  if (!enumMap && enumDefault === undefined) return value;
  if (value === undefined || value === null) return value;

  const source = String(value).trim();
  if (!source) return enumDefault !== undefined ? enumDefault : value;
  if (enumMap?.exact.has(source)) {
    return enumMap.exact.get(source);
  }

  const lowerMatch = enumMap?.lower.get(source.toLowerCase());
  if (lowerMatch !== undefined) {
    return lowerMatch;
  }

  if (enumDefault !== undefined) {
    return enumDefault;
  }
  return value;
};

const isRequestDateColumn = (column: any) =>
  String(column?.type || column?.columnType || "")
    .trim()
    .toLowerCase() === "date" ||
  column?.asDate === true ||
  column?.asDate === "true" ||
  String(column?.valueType || "")
    .trim()
    .toLowerCase() === "date";

const isRequestTimeColumn = (column: any) =>
  String(column?.type || column?.columnType || "")
    .trim()
    .toLowerCase() === "time" ||
  String(column?.valueType || "")
    .trim()
    .toLowerCase() === "time";

const isRequestIdentifierColumn = (column: any) =>
  column?.identifier === true ||
  column?.identifier === "true" ||
  String(column?.type || column?.columnType || "")
    .trim()
    .toLowerCase() === "identifier";

const toRequestIsoTimestamp = (value: unknown, column: any, mappingDef?: any): unknown => {
  if (!isRequestDateColumn(column)) return value;
  if (value === undefined || value === null) return value;
  const source = String(value).trim();
  if (!source) return value;

  const mappedBoundary = resolveCsvDateBoundaryValue(source, mappingDef);
  if (mappedBoundary) return mappedBoundary;

  const sourceFormat = getNonEmptyString(column?.dateFormat);
  const { date: parsed, matchedFormat } = sourceFormat
    ? parseCsvDateWithFormats(source, sourceFormat)
    : { date: moment.utc(source, moment.ISO_8601, true), matchedFormat: undefined };

  if (!parsed.isValid()) {
    const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
    throw new Error(
      sourceFormat
        ? `Unable to parse date '${source}' for column '${columnLabel}' with format '${sourceFormat}'`
        : `Unable to parse date '${source}' for column '${columnLabel}'`,
    );
  }

  if (matchedFormat && !csvDateFormatIncludesTime(matchedFormat)) return parsed.format("YYYY-MM-DD");

  return parsed.format("YYYY-MM-DD[T]HH:mm:ss.000[Z]");
};

const toRequestWallClockTime = (value: unknown, column: any): unknown => {
  if (!isRequestTimeColumn(column)) return value;
  if (value === undefined || value === null) return value;
  const source = String(value).trim();
  if (!source) return value;

  const sourceFormat = getNonEmptyString(column?.dateFormat) || getNonEmptyString(column?.timeFormat);
  const parsed = sourceFormat
    ? moment.utc(source, sourceFormat, true)
    : moment.utc(source, ["HH:mm:ss", "HHmmss", "Hmmss", "HH:mm", "Hmm"], true);

  if (!parsed.isValid()) {
    const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
    throw new Error(
      sourceFormat
        ? `Unable to parse time '${source}' for column '${columnLabel}' with format '${sourceFormat}'`
        : `Unable to parse time '${source}' for column '${columnLabel}'`,
    );
  }

  return parsed.format("HH:mm:ss");
};

const finalizeRequestColumnValue = (value: unknown, column: any, mappingDef?: any): unknown => {
  const unquoted = typeof value === "string" ? (parseQuotedLiteral(value) ?? value) : value;
  const mapped = applyRequestEnumMapping(unquoted, column);
  const normalizedEmail = normalizeMappedEmail(mapped, column);
  return toRequestWallClockTime(toRequestIsoTimestamp(normalizedEmail, column, mappingDef), column);
};

const withCsvRowContext = (error: unknown, rowNumber: number): Error => {
  const message = error instanceof Error ? error.message : String(error);
  if (/CSV row\s+\d+/i.test(message)) {
    return error instanceof Error ? error : new Error(message);
  }
  return new Error(`CSV row ${rowNumber}: ${message}`);
};

const getRequestColumnValue = (column: any, row: Record<string, any>, mappingDef?: any) => {
  if (column?.value !== undefined && !(typeof column.value === "string" && column.value.length === 0)) {
    const value =
      typeof column.value === "string" && column.value.includes("{{")
        ? processTemplate(column.value, row)
        : column.value;
    return finalizeRequestColumnValue(value, column, mappingDef);
  }

  const header = typeof column?.header === "string" ? column.header : "";
  if (!header) return undefined;

  if (header.includes("{{")) {
    const value = processTemplate(header, row);
    return finalizeRequestColumnValue(value, column, mappingDef);
  }

  const quotedLiteral = parseQuotedLiteral(header);
  if (quotedLiteral !== undefined) {
    return finalizeRequestColumnValue(quotedLiteral, column, mappingDef);
  }

  if (Object.prototype.hasOwnProperty.call(row, header)) {
    return finalizeRequestColumnValue(row[header], column, mappingDef);
  }

  return undefined;
};

/**
 * Map request data using a mapping definition
 * @param mappingDef - The mapping definition object (should be found before calling this)
 * @param release - The release object containing relationships
 * @param input - The input data to map (CSV string for CSV mode, or data object for JSON mode)
 */
export const mapRequestData = async (mappingDef: any, release: any, input: any): Promise<any> => {
  if (!mappingDef) return undefined;
  const def = mappingDef;
  const data: any = {};

  const addToEntity = (col: any, entityType: string, entity: any, entry: any) => {
    if (col.field && col.entity === entityType) {
      const value = getRequestColumnValue(col, entry, def);
      if (value !== undefined) {
        entity[col.field] = value;
      }
    }
  };

  const processMultilineRequest = (entry: any, currentEntities: any) => {
    const entityTypeKey = def.entityMapping.column;
    const entityType = def.entityMapping.mapping?.[entry[entityTypeKey]]
      ? def.entityMapping.mapping[entry[entityTypeKey]]
      : entry[entityTypeKey];

    if (!entityType) {
      return;
    }

    validateRequiredCsvColumns({ columns: def.columns.filter((column: any) => column.entity === entityType) }, [entry]);

    if (entityType === "global") {
      for (const col of def.columns) {
        addToEntity(col, entityType, data, entry);
      }
      return;
    }

    const parentChain = getParentChain(entityType, release.relationships);
    const pathParts = parentChain.split(".");

    const identifierColumn = def.columns.find((col: any) => isRequestIdentifierColumn(col) && col.entity === entityType);
    const identifierValue = identifierColumn ? getRequestColumnValue(identifierColumn, entry, def) : null;

    if (!identifierValue) {
      return;
    }

    // Coerce identifier to string so empty string / 0 from CSV don't skip the row
    const identifierStr = String(identifierValue).trim();
    if (!identifierStr) {
      return;
    }

    let currentLevel = data;
    for (const [index, part] of pathParts.entries()) {
      if (!currentLevel[part]) {
        currentLevel[part] = [];
      }

      if (index === pathParts.length - 1) {
        let entity = currentLevel[part].find((e: any) => e["@id"] === identifierStr);
        if (!entity) {
          entity = { "@id": identifierStr };
          currentLevel[part].push(entity);
        }
        for (const col of def.columns) {
          addToEntity(col, entityType, entity, entry);
        }
        currentEntities[entityType] = identifierStr;
      } else {
        let parentEntity = currentLevel[part].find((e: any) => e["@id"] === currentEntities[part]);
        if (!parentEntity) {
          const newParentId = uuid();
          parentEntity = { "@id": newParentId };
          currentLevel[part].push(parentEntity);
          currentEntities[part] = newParentId;
        }
        currentLevel = parentEntity;
      }
    }
  };

  const getSingleLineFieldName = (column: any): string => {
    const field = String(column?.field || "");
    return field.includes("@parent") ? field.split("/").pop() || field : field;
  };

  const getSingleLineEntityId = (entityColumns: any[], entry: any, fallbackFieldName?: string | null): string | null => {
    const identifierColumns = entityColumns.filter(isRequestIdentifierColumn);
    const idColumns = identifierColumns.length > 0
      ? identifierColumns
      : entityColumns.filter((column: any) => getSingleLineFieldName(column) === fallbackFieldName);

    const values = idColumns
      .map((column: any) => getRequestColumnValue(column, entry, def))
      .filter((value: any) => value !== undefined && value !== null)
      .map((value: any) => String(value).trim())
      .filter(Boolean);

    if (!values.length) return null;
    return values.length === 1 ? values[0] : JSON.stringify(values);
  };

  const processSingleLineRequest = (entry: any, entityPath: string[], entityMap: Map<string, any>, groups: any) => {
    let currentData = data;
    let currentPath = "";

    for (let i = 0; i < entityPath.length; i++) {
      const entityName = entityPath[i];
      const relationship = release.relationships.find((rel: any) => rel.name === entityName);
      const entityIdField = relationship ? relationship.textSingular : null;

      if (i < entityPath.length - 1) {
        const entityColumns = groups[entityName] || [];

        if (!currentData[entityName]) {
          currentData[entityName] = [];
        }

        let entityId = getSingleLineEntityId(entityColumns, entry, entityIdField);
        let entityData: any = null;

        if (!entityId) {
          entityId = uuid();
        }

        const contextPath = `${currentPath}/${entityName}/${entityId}`;

        if (entityMap.has(contextPath)) {
          entityData = entityMap.get(contextPath);
        } else {
          entityData = {
            ...cloneDeep(def.entityDefaults?.[entityName] || {}),
            "@id": entityId,
          };

          for (const column of entityColumns) {
            const fieldName = getSingleLineFieldName(column);
            const columnValue = getRequestColumnValue(column, entry, def);
            if (columnValue !== undefined) {
              entityData[fieldName] = columnValue;
            }
          }

          currentData[entityName].push(entityData);
          entityMap.set(contextPath, entityData);
        }

        currentPath = contextPath;
        currentData = entityData;
      } else {
        const entityColumns = groups[entityName] || [];

        if (!currentData[entityName]) {
          currentData[entityName] = [];
        }

        const entityId = getSingleLineEntityId(entityColumns, entry);
        const generatedEntityId = entityId || uuid();
        const contextPath = `${currentPath}/${entityName}/${generatedEntityId}`;

        let entityData: any;
        if (entityId && entityMap.has(contextPath)) {
          entityData = entityMap.get(contextPath);
        } else {
          entityData = {
            ...cloneDeep(def.entityDefaults?.[entityName] || {}),
            "@id": generatedEntityId,
          };
          currentData[entityName].push(entityData);
          if (entityId) {
            entityMap.set(contextPath, entityData);
          }
        }

        for (const column of entityColumns) {
          if (!column.field.includes("@parent")) {
            const columnValue = getRequestColumnValue(column, entry, def);
            if (columnValue !== undefined) {
              (entityData as any)[column.field] = columnValue;
            }
          }
        }
      }
    }
  };

  if ((def.mode || "").toLowerCase() === "csv") {
    if (typeof input === "string") {
      try {
        const parsedData = await parseCSV(input);
        if (def.entityMapping?.type !== "multiline") validateRequiredCsvColumns(def, parsedData);
        if (!Array.isArray(parsedData) || parsedData.length === 0) {
          throw new Error("Invalid CSV data");
        }

        const currentEntities: any = {};

        if (def.entityMapping.type === "singleline") {
          if (def.entityMapping.entityLine === "global") {
            const firstEntry = parsedData[0];
            if (firstEntry) {
              const globalColumns = def.columns.filter((c: any) => !c.field?.includes("@parent"));
              try {
                for (const col of globalColumns) {
                  if (col.field) {
                    const columnValue = getRequestColumnValue(col, firstEntry, def);
                    if (columnValue !== undefined) {
                      data[col.field] = columnValue;
                    }
                  }
                }
              } catch (error: unknown) {
                throw withCsvRowContext(error, 2);
              }
            }
          } else {
            const entityPath = getPathToEntity(def.entityMapping.entityLine, release.relationships);
            const globalColumns = def.columns.filter((column: any) => column.entity === "global");
            const firstEntry = parsedData[0];
            if (firstEntry) {
              try {
                for (const column of globalColumns) {
                  const columnValue = getRequestColumnValue(column, firstEntry, def);
                  if (columnValue !== undefined) {
                    data[getSingleLineFieldName(column)] = columnValue;
                  }
                }
              } catch (error: unknown) {
                throw withCsvRowContext(error, 2);
              }
            }

            const entityColumns = def.columns.filter((column: any) => !globalColumns.includes(column));
            const groups = groupBy(entityColumns, (c: any) => {
              if (c.field.includes("@parent")) {
                return c.field.split("/")[1];
              }
              return def.entityMapping.entityLine;
            });
            const entityMap = new Map<string, any>();

            for (const [entryIndex, entry] of parsedData.entries()) {
              try {
                processSingleLineRequest(entry, entityPath, entityMap, groups);
              } catch (error: unknown) {
                throw withCsvRowContext(error, entryIndex + 2);
              }
            }
          }
        } else {
          for (const [entryIndex, entry] of parsedData.entries()) {
            try {
              processMultilineRequest(entry, currentEntities);
            } catch (error: unknown) {
              throw withCsvRowContext(error, entryIndex + 2);
            }
          }
        }

        return data;
      } catch (error: any) {
        throw new Error(`Failed to process CSV input: ${error.message}`);
      }
    } else {
      throw new Error("CSV mode requires string input");
    }
  } else {
    // JSON mode
    try {
      return applyMapping(input, mappingDef);
    } catch (error: any) {
      console.log("mapping error", error);
      throw {
        message: `Error applying request mapping file ${error.message ? ": " + error.message : ""}`,
      };
    }
  }
};

/**
 * A session graph's values as request data. `graph`: any form DecisionGraph.from takes (a session graph read with its
 * release: sessionGraphWithRelease). Reads each node's fields rather than building node objects. `include`, when
 * given, limits it to the node ids it accepts.
 */
export const graphToData = (graph: unknown, include?: (id: number) => boolean) => {
  const g = DecisionGraph.from(graph as any);
  const data: any = {};
  const write = pathWriter(data);
  // each stored instance's row, by entity and the graph's instance: found once, not from its path for every value
  const rows = new Map<unknown, Map<number, any>>();

  const normalizePath = (path: unknown) => {
    if (!path) return "";
    return String(path).replace(/^\/+|\/+$/g, "");
  };

  for (const id of g.ids()) {
    if (g.field(id, "hidden")) continue;
    if (include && !include(id)) continue;
    const inferred = g.field(id, "inferred") as { type?: string } | undefined;
    if (inferred && inferred.type) continue; // These are special inferred nodes that contain the set of ids to inferred enities. Except if we include them in the data they will be merged with the actual sub entities - which will cause issues
    // a copy of an array or object value: rows are added to it below, and the graph's own value must stay as it is
    const read = g.result(id) as any;
    const value = Array.isArray(read) ? [...read] : read && typeof read === "object" ? { ...read } : read;
    const entity = g.field(id, "entity");
    const nodeId = g.field(id, "id") as string;
    const description = g.field(id, "description") as string;

    if (entity === "global") {
      data[nodeId] = value;
      data[description] = value;
      continue;
    }

    // a stored instance's index and parent path are its graph path's (as node(id) has them), read without building
    // the node; global and added nodes carry their own
    const own = g.isGlobal(id) || g.isAdded(id);
    let byInstance = own ? undefined : rows.get(entity);
    let row = byInstance?.get(g.instance(id));
    if (!row) {
      const parentPath = normalizePath(own ? g.field(id, "parent_path") : g.parentPath(id));
      const index = own ? g.field(id, "index") : g.index(id);
      const entityPath = [parentPath, entity, index].filter(Boolean).join("/");

      if (!entityPath) {
        data[nodeId] = value;
        data[description] = value;
        continue;
      }
      if (entityPath.split("/").length % 2 === 1) {
        // (not an instance's path: as setAtPath has it)
        setAtPath(data, `${entityPath}/${nodeId}`, value);
        setAtPath(data, `${entityPath}/${description}`, value);
        continue;
      }
      row = write.instance(entityPath);
      if (!own) {
        if (!byInstance) rows.set(entity, (byInstance = new Map()));
        byInstance.set(g.instance(id), row);
      }
    }
    write.attribute(row, nodeId, value);
    write.attribute(row, description, value);
  }

  return data;
};
